import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import api from '../utils/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { Clock, ChevronLeft, Share2, Tag, Bookmark, Search, Maximize2, X } from 'lucide-react';
import { showSuccess } from '../utils/toast';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingTime, setReadingTime] = useState(0);
  const [relatedNews, setRelatedNews] = useState([]);
  const [contentCategories, setContentCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/news/${slug}`);
        setNews(res.data);

        // Calculate reading time
        const text = stripHtmlTags(res.data.content || '');
        const words = text.trim().split(/\s+/).length;
        setReadingTime(Math.ceil(words / 200)); // ~200 words per minute

        // Fetch related news
        if (res.data) {
          fetchRelatedNews(res.data);
        }
      } catch (e) {
        if (e.response?.status === 404) setNews(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    fetchSidebarData();
  }, []);

  const fetchSidebarData = async () => {
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        api.get('/content-categories'),
        api.get('/content-tags')
      ]);
      setContentCategories(categoriesRes.data || []);
      setPopularTags((tagsRes.data || []).slice(0, 10));
    } catch (e) {
      console.error('Error fetching sidebar data:', e);
    }
  };

  const fetchRelatedNews = async (currentNews) => {
    try {
      const params = {
        status: 'published',
        limit: 6,
      };

      // Prioritize same category
      if (currentNews.contentCategory) {
        params.category = currentNews.contentCategory.slug;
      }

      const res = await api.get('/news', { params });

      // Filter out current news and get related ones
      let related = (res.data.news || []).filter(n => n._id !== currentNews._id);

      // If we have tags, boost news with matching tags
      if (currentNews.contentTags && currentNews.contentTags.length > 0) {
        const currentTagIds = currentNews.contentTags.map(t => t._id);
        related = related.sort((a, b) => {
          const aMatchingTags = (a.contentTags || []).filter(t => currentTagIds.includes(t._id)).length;
          const bMatchingTags = (b.contentTags || []).filter(t => currentTagIds.includes(t._id)).length;
          return bMatchingTags - aMatchingTags;
        });
      }

      setRelatedNews(related.slice(0, 3));
    } catch (e) {
      console.error('Error fetching related news:', e);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/news?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: news.title,
        text: `Check out this news: ${news.title}`,
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      showSuccess('Link copied to clipboard!');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <PageLoader message="Loading news..." />
    </div>
  );

  if (!news) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
          <Share2 className="w-10 h-10 text-gray-400" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">News Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">The news article you are looking for might have been removed or is temporarily unavailable.</p>
        <Link
          to="/news"
          className="px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition"
        >
          Back to News
        </Link>
      </div>
    );
  }

  const imgUrl = news.image
    ? news.image.startsWith('http')
      ? news.image
      : `${SITE_URL}${news.image.startsWith('/') ? '' : '/'}${news.image}`
    : undefined;

  return (
    <>
      <SEO
        title={`${news.title} – News`}
        description={news.excerpt ? news.excerpt.replace(/<[^>]*>/g, '').slice(0, 160) : undefined}
        image={imgUrl}
        canonical={`${SITE_URL}/news/${news.slug}`}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950">

        {/* Article Header (Hero) */}
        <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 pt-10 pb-16">
          <div className="container-custom">
            <Link to="/news" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-8 transition-colors">
              <ChevronLeft className="w-4 h-4" /> Back to News
            </Link>

            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-3">
                {news.contentCategory && (
                  <Link
                    to={`/news/category/${news.contentCategory.slug}`}
                    className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-bold uppercase tracking-wide rounded-full"
                  >
                    {news.contentCategory.name}
                  </Link>
                )}
                <span className="text-gray-400 text-sm flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {readingTime} min read
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight">
                {news.title}
              </h1>

              <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-6 mt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                    {news.author ? news.author.charAt(0) : 'A'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{news.author || 'Admin'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      {news.publishedAt && new Date(news.publishedAt).toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
                    title="Share news"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                  <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors">
                    <Bookmark className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image - Full Uncropped View */}
        {imgUrl && (
          <div className="container-custom -mt-10 mb-12 relative z-10">
            <div className="relative group rounded-2xl overflow-hidden shadow-2xl bg-gray-100 dark:bg-gray-900 border border-gray-200/60 dark:border-gray-800 flex items-center justify-center p-1 sm:p-2">
              <img
                src={imgUrl}
                alt={news.title}
                className="w-full h-auto max-h-[750px] object-contain rounded-xl mx-auto block cursor-pointer transition-transform duration-200 group-hover:scale-[1.005]"
                onClick={() => setLightboxOpen(true)}
              />
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="absolute bottom-4 right-4 bg-black/70 hover:bg-black/90 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 backdrop-blur-md opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg"
                title="View Full Size Image"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Full View
              </button>
            </div>
          </div>
        )}

        {/* Main Content Area with Sidebar */}
        <div className="container-custom pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* LEFT COLUMN: Main Article Content */}
            <div className="lg:col-span-8">
              <article className="prose prose-lg dark:prose-invert prose-primary max-w-none prose-img:rounded-xl prose-img:shadow-lg prose-img:w-full prose-img:h-auto prose-img:object-contain prose-headings:font-bold prose-a:text-primary-600 dark:prose-a:text-primary-400 hover:prose-a:text-primary-500">
                <div dangerouslySetInnerHTML={{ __html: news.content || '' }} />
              </article>

              {/* Tags */}
              {news.contentTags && news.contentTags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Related Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {news.contentTags.map((t) => (
                      <Link
                        key={t._id}
                        to={`/news/tag/${t.slug}`}
                        className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-colors text-sm font-medium"
                      >
                        #{t.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: Sticky Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-6">

                {/* Search Widget */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Search</h3>
                  <form onSubmit={handleSearch} className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search news..."
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                    <button
                      type="submit"
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-primary-600 transition-colors"
                    >
                      <Search className="w-5 h-5" />
                    </button>
                  </form>
                </div>

                {/* Categories Widget */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Categories</h3>
                  <div className="space-y-2">
                    <Link
                      to="/news"
                      className="block px-4 py-2.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                    >
                      All News
                    </Link>
                    {contentCategories.map((category) => (
                      <Link
                        key={category._id}
                        to={`/news/category/${category.slug}`}
                        className="block px-4 py-2.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Popular Tags Widget */}
                {popularTags.length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Tag className="w-5 h-5" /> Popular Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {popularTags.map((tag) => (
                        <Link
                          key={tag._id}
                          to={`/news/tag/${tag.slug}`}
                          className="px-3 py-1.5 rounded-full text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-colors"
                        >
                          #{tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>
        </div>

        {/* Related News - Full Width Below */}
        {relatedNews.length > 0 && (
          <div className="container-custom pb-20">
            <div className="border-t border-gray-100 dark:border-gray-800 pt-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-8">
                Related News
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedNews.map((relatedItem) => (
                  <Link
                    key={relatedItem._id}
                    to={`/news/${relatedItem.slug}`}
                    className="group block bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-800 transition-all duration-300"
                  >
                    {relatedItem.image && (
                      <div className="aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-gray-800">
                        <img
                          src={relatedItem.image}
                          alt={relatedItem.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      {relatedItem.contentCategory && (
                        <span className="inline-block px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-bold uppercase tracking-wide rounded mb-3">
                          {relatedItem.contentCategory.name}
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {relatedItem.title}
                      </h3>
                      {relatedItem.excerpt && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {stripHtmlTags(relatedItem.excerpt)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Newsletter / CTA */}
        <div className="container-custom pb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 dark:from-primary-900 dark:to-indigo-900 rounded-2xl p-8 md:p-12 text-center text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-3">Stay in the loop</h3>
              <p className="text-gray-300 mb-8 max-w-md mx-auto">
                Get the latest business insights and local news from Fatehabad delivered directly to your inbox.
              </p>
              <div className="flex max-w-md mx-auto gap-3">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all"
                />
                <button className="px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-bold rounded-xl transition-colors shadow-lg shadow-primary-500/30">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Size Image Lightbox Modal */}
      {lightboxOpen && imgUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-2 sm:p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative max-w-[95vw] max-h-[92vh] flex items-center justify-center">
            <img
              src={imgUrl}
              alt={news.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default NewsDetailPage;
